import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  FileText,
  Eye,
  Pencil,
  Trash2,
  BookOpen,
  HardDrive,
  Tag,
  Shield,
  ChevronDown,
  Info,
} from "lucide-react";

const LEVEL_LABELS = {
  mam_non: "Mầm non",
  tieu_hoc: "Tiểu học",
  thcs: "THCS",
  thpt: "THPT",
};

const TYPE_LABELS = {
  giaotrinh: "Giáo trình",
  giao_trinh: "Giáo trình",
  baigiang: "Bài giảng",
  bai_giang: "Bài giảng",
  baitap: "Bài tập",
  bai_tap: "Bài tập",
  video: "Video bài học",
  dethi: "Đề kiểm tra",
  de_thi: "Đề kiểm tra",
  khac: "Khác",
};

const SUBJECT_LABELS = {
  ky_nang_song: "Kỹ Năng Sống",
  toan_tu_duy: "Toán Tư Duy",
  tieng_anh_tang_cuong: "Tiếng Anh Tăng Cường",
};

const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + " GB";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + " MB";
  if (n >= 1_000) return (n / 1_000).toFixed(2) + " KB";
  return n + " B";
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function isZipDocument(doc) {
  if (!doc) return false;
  const ext = String(doc.file_extension ?? "")
    .trim()
    .replace(/^\./, "")
    .toLowerCase();
  if (ext === "zip") return true;
  if (String(doc.file_name || "")
    .toLowerCase()
    .endsWith(".zip"))
    return true;
  const mime = String(doc.file_type || "").toLowerCase();
  return (
    mime.includes("zip") ||
    mime === "application/x-zip-compressed" ||
    mime === "multipart/x-zip"
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-3 last:border-0">
      <span className="shrink-0 text-sm text-slate-500">{label}</span>
      <span
        className={`max-w-[65%] text-right text-sm font-semibold text-slate-800 break-words ${
          mono ? "font-mono text-xs text-slate-600" : ""
        }`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

/**
 * Modal chi tiết tài liệu học tập (elearning)
 */
const DocumentDetailModal = ({
  document: doc,
  onClose,
  onView,
  onEdit,
  onDelete,
  canView,
  canEdit,
  canDelete,
}) => {
  const [showTech, setShowTech] = useState(false);

  if (!doc) return null;

  const subjectLabel = SUBJECT_LABELS[doc.subject] || doc.subject || "—";
  const typeLabel = TYPE_LABELS[doc.type] || doc.type || "—";
  const levelLabel = LEVEL_LABELS[doc.level] || doc.level || null;

  const subjectName =
    (typeof doc.subject_name === "string" && doc.subject_name.trim()) ||
    subjectLabel;
  const subjectCode =
    (typeof doc.subject_code === "string" && doc.subject_code.trim()) ||
    (typeof doc.subject === "string" && doc.subject.trim()) ||
    "";

  const courseName =
    (typeof doc.course_name === "string" && doc.course_name.trim()) ||
    (doc.course_id ? "—" : "Dùng chung môn học");
  const courseCode = doc.course_code?.trim() || "";

  const isActive = Boolean(doc.is_active);
  const zipNoInlineView = isZipDocument(doc);
  const ext = String(doc.file_extension || "")
    .replace(/^\./, "")
    .toUpperCase();

  return (
    <AnimatePresence>
      <motion.div
        key="edu-doc-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-slate-900/55 backdrop-blur-md"
        onClick={onClose}
      />
      <motion.div
        key="edu-doc-panel"
        initial={{ opacity: 0, scale: 0.97, y: 14 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 10 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none fixed inset-0 z-[210] flex items-center justify-center p-3 sm:p-4"
      >
        <div
          className="pointer-events-auto flex max-h-[min(92vh,820px)] w-full max-w-4xl flex-col overflow-hidden rounded-[1.75rem] border border-slate-100 bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="shrink-0 border-b border-slate-100 bg-gradient-to-r from-red-50 via-white to-white px-6 py-5 md:px-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-600 text-white shadow-lg shadow-red-200">
                  <FileText size={22} />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-black leading-snug tracking-tight text-slate-800 sm:text-xl">
                    {doc.file_name}
                  </h2>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${
                        isActive
                          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                          : "border-slate-200 bg-slate-100 text-slate-600"
                      }`}
                    >
                      {isActive ? "Đang sử dụng" : "Ngưng dùng"}
                    </span>
                    {ext ? (
                      <span className="inline-flex rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold uppercase text-slate-600">
                        .{ext.toLowerCase()}
                      </span>
                    ) : null}
                    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                      <Tag size={12} />
                      {typeLabel}
                    </span>
                    {doc.code ? (
                      <span className="font-mono text-xs text-slate-400">
                        {doc.code}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 rounded-xl p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                aria-label="Đóng"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-6 py-5 md:px-8">
            <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1.4fr)_300px]">
              {/* Left — business info */}
              <div className="space-y-4">
                <section className="rounded-[1.5rem] border border-slate-100 bg-white p-5 shadow-sm">
                  <h3 className="mb-1 text-base font-bold text-slate-800">
                    Thông tin phân loại
                  </h3>
                  <p className="mb-3 text-sm text-slate-500">
                    Môn học, khóa học và ngữ cảnh sử dụng tài liệu.
                  </p>
                  <div className="divide-y divide-slate-100">
                    <InfoRow
                      label="Môn học"
                      value={
                        subjectCode
                          ? `${subjectName} (${subjectCode})`
                          : subjectName
                      }
                    />
                    <InfoRow
                      label="Khóa học"
                      value={
                        courseCode
                          ? `${courseName} · ${courseCode}`
                          : courseName
                      }
                    />
                    <InfoRow label="Phân loại" value={typeLabel} />
                    {levelLabel ? (
                      <InfoRow label="Trình độ" value={levelLabel} />
                    ) : null}
                  </div>
                </section>

                <section className="rounded-[1.5rem] border border-slate-100 bg-white p-5 shadow-sm">
                  <h3 className="mb-3 text-base font-bold text-slate-800">
                    Mô tả
                  </h3>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                    {doc.description?.trim()
                      ? doc.description
                      : "Không có mô tả."}
                  </p>
                </section>

                <section className="rounded-[1.5rem] border border-slate-100 bg-white p-5 shadow-sm">
                  <h3 className="mb-1 text-base font-bold text-slate-800">
                    Nhật ký
                  </h3>
                  <div className="divide-y divide-slate-100">
                    <InfoRow
                      label="Ngày tạo"
                      value={formatDateTime(doc.created_at || doc.create_at)}
                    />
                    <InfoRow
                      label="Cập nhật lần cuối"
                      value={formatDateTime(doc.last_changed)}
                    />
                  </div>
                </section>

                {/* Technical — collapsed */}
                <div className="overflow-hidden rounded-[1.5rem] border border-slate-100 bg-slate-50/80">
                  <button
                    type="button"
                    onClick={() => setShowTech((v) => !v)}
                    className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left"
                  >
                    <span className="flex items-center gap-2 text-sm font-bold text-slate-600">
                      <Info size={16} className="text-slate-400" />
                      Thông tin kỹ thuật
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-slate-400 transition-transform ${
                        showTech ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {showTech ? (
                    <div className="border-t border-slate-100 bg-white px-5 pb-2">
                      <InfoRow label="MIME" value={doc.file_type || "—"} mono />
                      <InfoRow label="ID tài liệu" value={doc.id} mono />
                      {doc.course_id ? (
                        <InfoRow
                          label="ID khóa học"
                          value={doc.course_id}
                          mono
                        />
                      ) : null}
                      {doc.created_by || doc.create_by ? (
                        <InfoRow
                          label="Người tạo (ID)"
                          value={doc.created_by || doc.create_by}
                          mono
                        />
                      ) : null}
                      {doc.updated_by || doc.update_by ? (
                        <InfoRow
                          label="Người cập nhật (ID)"
                          value={doc.updated_by || doc.update_by}
                          mono
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Right — summary */}
              <div className="space-y-4 lg:sticky lg:top-0">
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-900 p-5 text-white shadow-xl shadow-slate-900/10">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                      <HardDrive size={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/60">
                        Tệp đính kèm
                      </p>
                      <p className="truncate text-base font-bold">
                        {ext ? `.${ext.toLowerCase()}` : "Tài liệu"}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex justify-between gap-3 text-sm">
                      <span className="text-white/60">Dung lượng</span>
                      <span className="font-semibold">
                        {formatFileSize(doc.file_size)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3 text-sm">
                      <span className="text-white/60">Trạng thái</span>
                      <span className="font-semibold">
                        {isActive ? "Đang sử dụng" : "Ngưng dùng"}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3 text-sm">
                      <span className="text-white/60">Phân loại</span>
                      <span className="max-w-[55%] text-right font-semibold">
                        {typeLabel}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-[1.5rem] border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="mb-3 flex items-center gap-2">
                    <BookOpen size={16} className="text-red-500" />
                    <h3 className="font-bold text-slate-800">Ngữ cảnh</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="rounded-2xl bg-slate-50 px-3.5 py-3">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        Môn học
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">
                        {subjectName}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-3.5 py-3">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        Khóa học
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">
                        {courseName}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-[1.5rem] border border-amber-100 bg-amber-50 p-4">
                  <div className="flex items-start gap-2.5">
                    <Shield
                      size={16}
                      className="mt-0.5 shrink-0 text-amber-600"
                    />
                    <p className="text-xs leading-relaxed text-amber-800">
                      {zipNoInlineView
                        ? "File ZIP không xem trước trong app — gắn qua module Bài học để trình chiếu."
                        : "Chỉ PDF được xem trước trong ứng dụng. Các định dạng khác mở qua module Bài học khi cần."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-slate-100 bg-slate-50/80 px-6 py-4 md:px-8">
            <div className="flex flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={onClose}
                className="rounded-2xl px-5 py-2.5 text-sm font-bold text-slate-500 transition hover:text-red-600"
              >
                Đóng
              </button>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                {canEdit ? (
                  <button
                    type="button"
                    onClick={onEdit}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
                  >
                    <Pencil size={16} />
                    Sửa
                  </button>
                ) : null}
                {canDelete ? (
                  <button
                    type="button"
                    onClick={onDelete}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200"
                  >
                    <Trash2 size={16} />
                    Xoá
                  </button>
                ) : null}
                {canView && !zipNoInlineView ? (
                  <button
                    type="button"
                    onClick={onView}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-red-200 transition hover:bg-red-700 active:scale-[0.98]"
                  >
                    <Eye size={16} />
                    Xem tài liệu
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DocumentDetailModal;
